import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaTimes, FaBox } from 'react-icons/fa';
import assetService from '../services/assetService';
import MaintenanceHistory from './MaintenanceHistory';
import EditUnitModal from './EditUnitModal';
import '../styles/ViewAssets.css';
import AssistantSidebar from './AssistantSidebar';
import { API_BASE_URL } from '../config/api';

const ViewAssets = ({ embedded = false, showCreatedBy = false, title = 'Asset Inventory' }) => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [allocationAsset, setAllocationAsset] = useState(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocations, setAllocations] = useState({ available: [], allocated: [] });
  const [allocationLoading, setAllocationLoading] = useState(false);

  // Filter states
  const [filters] = useState({
    search: '',
    department: '',
    category: '',
    status: ''
  });

  const [categories] = useState([]);
  const [departments] = useState([]);

  // Fetch assets on component mount
  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/assets`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setError('Failed to load assets');
        setAssets([]);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError(err.response?.data?.message || 'Failed to load assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAsset(null);
  };

  const handleAssetHistory = (assetId) => {
    navigate(`/asset-history/${assetId}`);
  };

  const toggleAssetDetails = (asset) => {
    setSelectedAsset(asset);
    setShowModal(true);
  };

  const handleFilterChange = () => {
    // Filter functionality not fully implemented
  };

  const handleResetFilters = () => {
    // Reset filters functionality
  };

  const handleViewAllocations = async (asset) => {
    setAllocationAsset(asset);
    setAllocationLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Fetch allocation details for this asset
      const allocationRes = await axios.get(
        `${API_BASE_URL}/api/asset-users/allocations?assetId=${asset._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (allocationRes.data.success) {

        setAllocations({
          available: allocationRes.data.data.available || [],
          allocated: allocationRes.data.data.allocated || []
        });
      } else {
          setError('Failed to load allocation details - invalid response format');
      }
      
      setShowAllocationModal(true);
    } catch (err) {
      console.error('Error fetching allocations:', err);
      setError('Failed to load allocation details');
    } finally {
      setAllocationLoading(false);
    }
  };

  const handleAssetUpdated = (updatedAsset) => {
    setAssets(assets.map(a => a._id === updatedAsset._id ? updatedAsset : a));
    setSelectedAsset(updatedAsset);
  };

  return (
    <div className={`view-assets-page ${embedded ? 'embedded' : ''}`}>
      {!embedded && <AssistantSidebar user={null} />}
      <div className="view-assets-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700 }}>{title}</h2>
          {!embedded && (
            <button
              onClick={() => navigate('/add-asset')}
              className=" btn-primary"
              style={{ whiteSpace: 'nowrap', padding: '0.75rem 1.5rem', fontWeight: 600, fontSize: '0.95rem', width: 'fit-content' }}
            >
            ADD ASSET
            </button>
          )}
        </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="search">Search (Name / Serial Number)</label>
            <input
              type="text"
              id="search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search assets..."
            />
          </div>

          <div className="filter-group">
            <label htmlFor="department">Department</label>
            <select
              id="department"
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All Departments</option>
              {departments.map((dept, idx) => (
                <option key={idx} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
            >
              <option value="">All Categories</option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="under_maintenance">Under Maintenance</option>
              <option value="disposed">Disposed</option>
            </select>
          </div>
        </div>

        <button onClick={handleResetFilters} className="btn btn-secondary">
          Reset Filters
        </button>
      </div>

      {/* Assets Table */}
      {loading ? (
        <div className="loading">Loading assets...</div>
      ) : assets.length === 0 ? (
        <div className="no-assets">No assets found</div>
      ) : (
        <div className="assets-table-responsive">
          <table className="assets-table">
            <thead>
              <tr>
                <th>Asset Name</th>
                <th>Category</th>
                <th>Type</th>
                <th>Serial No</th>
                <th>Department</th>
                <th>Condition</th>
                <th>Quantity</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset._id} className={asset.isLowStock ? 'low-stock' : ''}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="asset-image-cell" style={{ width: '40px', minWidth: '40px', height: '40px' }}>
                          {asset.assetImage ? (
                            <img src={asset.assetImage} alt={asset.assetName} className="asset-thumbnail" style={{ width: '40px', height: '40px', borderRadius: '4px' }} />
                          ) : (
                            <div className="asset-icon-placeholder" style={{ width: '40px', height: '40px' }}>
                              <FaBox size={20} />
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <strong style={{ display: 'block', fontSize: '13px' }}>{asset.assetName}</strong>
                          {asset.isWarrantyExpiring && (
                            <span className="warning-badge" style={{ fontSize: '11px' }}>Warranty Expiring</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{asset.category}</td>
                    <td>{asset.assetType}</td>
                    <td style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monaco' }}>{asset.serialNumber || '-'}</td>
                    <td>{asset.department}</td>
                    <td>{asset.condition}</td>
                    <td style={{ textAlign: 'center' }}>{asset.quantity}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => toggleAssetDetails(asset)}
                          title="View or Edit asset"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleViewAllocations(asset)}
                          title="View allocation details"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                          Allocate
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAssetHistory(asset._id)}
                          title="View asset history"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                          History
                        </button>
                      </div>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Asset View/Edit Modal */}
      {showModal && selectedAsset && (
        <AssetModal
          asset={selectedAsset}
          onClose={handleModalClose}
          onAssetUpdated={handleAssetUpdated}
          onAssetDeleted={(id) => {
            setAssets(assets.filter(a => a._id !== id));
            handleModalClose();
          }}
        />
      )}

      {/* Allocation Details Modal */}
      {showAllocationModal && allocationAsset && (
        <AllocationDetailsModal
          asset={allocationAsset}
          allocations={allocations}
          loading={allocationLoading}
          onClose={() => setShowAllocationModal(false)}
        />
      )}
      </div>
    </div>
  );
};

/* ======================== ASSET MODAL ======================== */
const AssetModal = ({ asset, onClose, onAssetUpdated, onAssetDeleted }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    assetName: asset.assetName || '',
    category: asset.category || '',
    assetType: asset.assetType || 'non-consumable',
    brand: asset.brand || '',
    model: asset.model || '',
    serialNumber: asset.serialNumber || '',
    quantity: asset.quantity || 1,
    minQuantity: asset.minQuantity || 0,
    labLocation: asset.labLocation || '',
    purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
    purchaseCost: asset.purchaseCost || '',
    warrantyExpiryDate: asset.warrantyExpiryDate ? asset.warrantyExpiryDate.split('T')[0] : '',
    status: asset.status || 'available',
    condition: asset.condition || 'good',
    supplier: asset.supplier || '',
    invoiceNumber: asset.invoiceNumber || '',
    invoiceImage: asset.invoiceImage || '',
    assetImage: asset.assetImage || '',
    maintenanceCycleDays: asset.maintenanceCycleDays || '',
    notes: asset.notes || '',
    specifications: asset.specifications ? { ...asset.specifications } : {},
    maintenanceChecklists: asset.maintenanceChecklists || [],
    complianceChecklists: asset.complianceChecklists || []
  });
  const [invoiceImagePreview, setInvoiceImagePreview] = useState(asset.invoiceImage || '');
  const [assetImagePreview, setAssetImagePreview] = useState(asset.assetImage || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUnitEditModal, setShowUnitEditModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(null);
  const [currentAsset, setCurrentAsset] = useState(asset);
  const [maintenanceChecklists, setMaintenanceChecklists] = useState([]);
  const [complianceChecklists, setComplianceChecklists] = useState([]);
  const [newMaintenanceItem, setNewMaintenanceItem] = useState('');
  const [newComplianceItem, setNewComplianceItem] = useState('');

  // Load checklists from asset data when modal opens
  useEffect(() => {
    try {
      // Load checklists directly from asset model (new storage approach)
      if (asset.maintenanceChecklists && Array.isArray(asset.maintenanceChecklists)) {
     
        setMaintenanceChecklists(asset.maintenanceChecklists);
      } else {
        setMaintenanceChecklists([]);
      }
      
      if (asset.complianceChecklists && Array.isArray(asset.complianceChecklists)) {
      
        setComplianceChecklists(asset.complianceChecklists);
      } else {
        setComplianceChecklists([]);
      }
    } catch (err) {
      console.error('Error loading checklists:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset._id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSpecificationChange = (specKey, value) => {
    setFormData({
      ...formData,
      specifications: {
        ...formData.specifications,
        [specKey]: value
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, invoiceImage: reader.result });
        setInvoiceImagePreview(reader.result);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, invoiceImage: '' });
    setInvoiceImagePreview('');
  };

  const handleAssetImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, assetImage: reader.result });
        setAssetImagePreview(reader.result);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAssetImage = () => {
    setFormData({ ...formData, assetImage: '' });
    setAssetImagePreview('');
  };

  const handleAddMaintenanceChecklistItem = () => {
    if (newMaintenanceItem.trim()) {
      setMaintenanceChecklists([
        ...maintenanceChecklists,
        { text: newMaintenanceItem.trim(), completed: false }
      ]);
      setNewMaintenanceItem('');
    }
  };

  const handleRemoveMaintenanceChecklistItem = (index) => {
    setMaintenanceChecklists(maintenanceChecklists.filter((_, i) => i !== index));
  };

  const handleToggleMaintenanceChecklistItem = (index) => {
    const updated = [...maintenanceChecklists];
    updated[index].completed = !updated[index].completed;
    setMaintenanceChecklists(updated);
  };

  const handleAddComplianceChecklistItem = () => {
    if (newComplianceItem.trim()) {
      setComplianceChecklists([
        ...complianceChecklists,
        { text: newComplianceItem.trim(), completed: false }
      ]);
      setNewComplianceItem('');
    }
  };

  const handleRemoveComplianceChecklistItem = (index) => {
    setComplianceChecklists(complianceChecklists.filter((_, i) => i !== index));
  };

  const handleToggleComplianceChecklistItem = (index) => {
    const updated = [...complianceChecklists];
    updated[index].completed = !updated[index].completed;
    setComplianceChecklists(updated);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Include current checklist states in formData before saving
      const updateData = {
        ...formData,
        maintenanceChecklists: maintenanceChecklists || [],
        complianceChecklists: complianceChecklists || []
      };

      // Update asset including checklists
      const response = await assetService.updateAsset(asset._id, updateData);
      const updatedAsset = response.data?.data || response.data;

     
      setSuccess('Asset and checklists updated successfully!');
      setIsEditing(false);
      
      // Reload asset data to reflect all changes
      if (onAssetUpdated) onAssetUpdated(updatedAsset);
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Failed to update asset');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetService.deleteAsset(asset._id);
        if (onAssetDeleted) onAssetDeleted(asset._id);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete asset');
      }
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original asset values
    setFormData({
      assetName: asset.assetName || '',
      category: asset.category || '',
      assetType: asset.assetType || 'non-consumable',
      brand: asset.brand || '',
      model: asset.model || '',
      serialNumber: asset.serialNumber || '',
      quantity: asset.quantity || 1,
      minQuantity: asset.minQuantity || 0,
      labLocation: asset.labLocation || '',
      purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
      purchaseCost: asset.purchaseCost || '',
      warrantyExpiryDate: asset.warrantyExpiryDate ? asset.warrantyExpiryDate.split('T')[0] : '',
      status: asset.status || 'available',
      condition: asset.condition || 'good',
      supplier: asset.supplier || '',
      invoiceNumber: asset.invoiceNumber || '',
      invoiceImage: asset.invoiceImage || '',
      assetImage: asset.assetImage || '',
      maintenanceCycleDays: asset.maintenanceCycleDays || '',
      notes: asset.notes || '',
      specifications: asset.specifications ? { ...asset.specifications } : {},
      maintenanceChecklists: asset.maintenanceChecklists || [],
      complianceChecklists: asset.complianceChecklists || []
    });
    
    // Reset checklist state arrays
    setMaintenanceChecklists(asset.maintenanceChecklists || []);
    setComplianceChecklists(asset.complianceChecklists || []);
    setNewMaintenanceItem('');
    setNewComplianceItem('');
    
    setInvoiceImagePreview(asset.invoiceImage || '');
    setAssetImagePreview(asset.assetImage || '');
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleEditUnit = (unit, unitIndex) => {
    setSelectedUnit(unit);
    setSelectedUnitIndex(unitIndex);
    setShowUnitEditModal(true);
  };

  const handleUnitEditClose = () => {
    setShowUnitEditModal(false);
    setSelectedUnit(null);
    setSelectedUnitIndex(null);
  };

  const handleUnitUpdated = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/assets/${asset._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data) {
        setCurrentAsset(response.data.data);
        setSuccess('Unit updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to reload asset data');
    }
  };

  return (
    <div className="asset-modal-overlay" onClick={onClose}>
      <div className="asset-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="asset-modal-header">
          <h3>{isEditing ? 'Edit Asset' : 'Asset Details'}</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Modal Body */}
        <div className="asset-modal-body">
          {/* Basic Information */}
          <div className="modal-section">
            <h4 className="modal-section-title">Basic Information</h4>
            <div className="modal-fields-grid">
              <div className="modal-field">
                <label>Asset Name *</label>
                {isEditing ? (
                  <input type="text" name="assetName" value={formData.assetName} onChange={handleInputChange} required />
                ) : (
                  <span>{formData.assetName || '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Category *</label>
                {isEditing ? (
                  <input type="text" name="category" value={formData.category} onChange={handleInputChange} required />
                ) : (
                  <span>{formData.category || '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Asset Type *</label>
                {isEditing ? (
                  <select name="assetType" value={formData.assetType} onChange={handleInputChange}>
                    <option value="non-consumable">Non-Consumable</option>
                    <option value="consumable">Consumable</option>
                  </select>
                ) : (
                  <span className="capitalize">{formData.assetType}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Brand</label>
                {isEditing ? (
                  <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} />
                ) : (
                  <span>{formData.brand || '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Model</label>
                {isEditing ? (
                  <input type="text" name="model" value={formData.model} onChange={handleInputChange} />
                ) : (
                  <span>{formData.model || '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Serial Number</label>
                {isEditing ? (
                  <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} />
                ) : (
                  <span>{formData.serialNumber || '-'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Specifications */}
          {Object.keys(formData.specifications).length > 0 && (
            <div className="modal-section">
              <h4 className="modal-section-title">Specifications</h4>
              <div className="modal-fields-grid">
                {Object.entries(formData.specifications).map(([specKey, specValue]) => (
                  <div key={specKey} className="modal-field">
                    <label>{specKey}</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={specValue || ''}
                        onChange={(e) => handleSpecificationChange(specKey, e.target.value)}
                        placeholder={`Enter ${specKey}`}
                      />
                    ) : (
                      <span>{specValue || '-'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inventory Information */}
          <div className="modal-section">
            <h4 className="modal-section-title">Inventory Information</h4>
            <div className="modal-fields-grid">
              <div className="modal-field">
                <label>Quantity</label>
                {isEditing ? (
                  <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} min="1" />
                ) : (
                  <span>{formData.quantity}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Min Quantity (Low Stock Alert)</label>
                {isEditing ? (
                  <input type="number" name="minQuantity" value={formData.minQuantity} onChange={handleInputChange} min="0" />
                ) : (
                  <span>{formData.minQuantity}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Department *</label>
                {isEditing ? (
                  <input type="text" name="department" value={formData.department} onChange={handleInputChange} required />
                ) : (
                  <span>{formData.department || '-'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Purchase & Warranty Information */}
          <div className="modal-section">
            <h4 className="modal-section-title">Purchase & Warranty</h4>
            <div className="modal-fields-grid">
              <div className="modal-field">
                <label>Purchase Date</label>
                {isEditing ? (
                  <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleInputChange} />
                ) : (
                  <span>{formData.purchaseDate ? new Date(formData.purchaseDate).toLocaleDateString() : '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Purchase Cost</label>
                {isEditing ? (
                  <input type="number" name="purchaseCost" value={formData.purchaseCost} onChange={handleInputChange} step="0.01" />
                ) : (
                  <span>{formData.purchaseCost ? `₹${Number(formData.purchaseCost).toFixed(2)}` : '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Warranty Expiry Date</label>
                {isEditing ? (
                  <input type="date" name="warrantyExpiryDate" value={formData.warrantyExpiryDate} onChange={handleInputChange} />
                ) : (
                  <span>{formData.warrantyExpiryDate ? new Date(formData.warrantyExpiryDate).toLocaleDateString() : '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Supplier</label>
                {isEditing ? (
                  <input type="text" name="supplier" value={formData.supplier} onChange={handleInputChange} />
                ) : (
                  <span>{formData.supplier || '-'}</span>
                )}
              </div>

              <div className="modal-field">
                <label>Invoice Number</label>
                {isEditing ? (
                  <input type="text" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleInputChange} />
                ) : (
                  <span>{formData.invoiceNumber || '-'}</span>
                )}
              </div>

              <div className="modal-field full-width">
                <label>Asset Image</label>
                {isEditing ? (
                  <div className="invoice-edit-area">
                    <input type="file" accept="image/*" onChange={handleAssetImageChange} />
                    <small className="help-text">Max size: 5MB (JPG, PNG, etc.)</small>
                    {assetImagePreview && (
                      <div className="invoice-preview-edit">
                        <img src={assetImagePreview} alt="Asset Preview" />
                        <button type="button" className="btn btn-sm btn-danger" onClick={handleRemoveAssetImage}>
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  assetImagePreview ? (
                    <div className="invoice-image-display">
                      <img
                        src={assetImagePreview}
                        alt="Asset"
                        onClick={() => window.open(assetImagePreview)}
                        title="Click to view full size"
                      />
                    </div>
                  ) : (
                    <span>-</span>
                  )
                )}
              </div>

              <div className="modal-field full-width">
                <label>Invoice Image</label>
                {isEditing ? (
                  <div className="invoice-edit-area">
                    <input type="file" accept="image/*" onChange={handleImageChange} />
                    <small className="help-text">Max size: 5MB (JPG, PNG, etc.)</small>
                    {invoiceImagePreview && (
                      <div className="invoice-preview-edit">
                        <img src={invoiceImagePreview} alt="Invoice Preview" />
                        <button type="button" className="btn btn-sm btn-danger" onClick={handleRemoveImage}>
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  invoiceImagePreview ? (
                    <div className="invoice-image-display">
                      <img
                        src={invoiceImagePreview}
                        alt="Invoice"
                        onClick={() => window.open(invoiceImagePreview)}
                        title="Click to view full size"
                      />
                    </div>
                  ) : (
                    <span>-</span>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Status & Condition */}
          <div className="modal-section">
            <h4 className="modal-section-title">Status & Condition</h4>
            <div className="modal-fields-grid">
              <div className="modal-field">
                <label>Status</label>
                {isEditing ? (
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="available">Available</option>
                    <option value="in_use">In Use</option>
                    <option value="under_maintenance">Under Maintenance</option>
                    <option value="disposed">Disposed</option>
                  </select>
                ) : (
                  <span className={`status-badge-modal status-${formData.status}`}>
                    {formData.status?.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>

              <div className="modal-field">
                <label>Condition</label>
                {isEditing ? (
                  <select name="condition" value={formData.condition} onChange={handleInputChange}>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="damaged">Damaged</option>
                  </select>
                ) : (
                  <span className="capitalize">{formData.condition}</span>
                )}
              </div>

              <div className="modal-field full-width">
                <label>Notes</label>
                {isEditing ? (
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" placeholder="Additional notes" />
                ) : (
                  <span>{formData.notes || '-'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Maintenance Cycle (Non-consumable only) */}
          {formData.assetType === 'non-consumable' && (
            <div className="modal-section">
              <h4 className="modal-section-title">Maintenance</h4>
              <div className="modal-fields-grid">
                <div className="modal-field">
                  <label>Maintenance Cycle (Days)</label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="maintenanceCycleDays"
                      value={formData.maintenanceCycleDays}
                      onChange={handleInputChange}
                      min="30"
                      placeholder="Min 30 days"
                    />
                  ) : (
                    <span>{formData.maintenanceCycleDays ? `Every ${formData.maintenanceCycleDays} days` : '-'}</span>
                  )}
                </div>

                <div className="modal-field">
                  <label>Last Maintenance Date</label>
                  <span>{asset.lastMaintenanceDate ? new Date(asset.lastMaintenanceDate).toLocaleDateString() : 'Not yet serviced'}</span>
                </div>

                <div className="modal-field">
                  <label>Next Maintenance Due</label>
                  <span className={asset.nextMaintenanceDue && new Date(asset.nextMaintenanceDue) < new Date() ? 'overdue' : ''}>
                    {asset.nextMaintenanceDue ? new Date(asset.nextMaintenanceDue).toLocaleDateString() : 'Not scheduled'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Units Information */}
          {currentAsset.units && currentAsset.units.length > 0 && (
            <div className="modal-section">
              <h4 className="modal-section-title">📦 Units ({currentAsset.units.length})</h4>
              <div style={{ display: 'grid', gap: '15px' }}>
                {currentAsset.units.map((unit, index) => (
                  <div key={unit._id || index} style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: '#f7fafc'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <div>
                        <strong style={{ fontSize: '14px' }}>Unit ID: {unit.unitId}</strong>
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                          Status: <span className={`status-badge-modal status-${unit.status}`}>
                            {unit.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditUnit(unit, index)}
                        className="btn btn-sm btn-primary"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        ✏️ Edit Unit
                      </button>
                    </div>

                    {unit.image && (
                      <div style={{ marginBottom: '10px' }}>
                        <img
                          src={unit.image}
                          alt={unit.unitId}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '120px',
                            borderRadius: '6px'
                          }}
                        />
                      </div>
                    )}

                    {unit.specifications && Object.keys(unit.specifications).length > 0 && (
                      <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                        <strong>Specifications:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                          {Object.entries(unit.specifications).map(([key, value]) => (
                            <li key={key}><strong>{key}:</strong> {value || '-'}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Maintenance Checklist */}
          <div className="modal-section">
              <h4 className="modal-section-title">🔧 Maintenance Checklist</h4>
              <div className="checklist-container">
                {/* View Mode - Read Existing Checklists */}
                {!isEditing && maintenanceChecklists.length > 0 && (
                  <div>
                    <div className="checklist-existing-header">Existing Items:</div>
                    <ul className="checklist-items-list">
                      {maintenanceChecklists.map((item, index) => (
                        <li key={index} className="checklist-item read-only">
                          <input
                            type="checkbox"
                            checked={item.completed || false}
                            readOnly
                          />
                          <span className="checklist-item-text">
                            {item.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Edit Mode - Full CRUD */}
                {isEditing && (
                  <div>
                    {maintenanceChecklists.length > 0 && (
                      <div>
                        <div className="checklist-existing-header">Existing Items:</div>
                        <ul className="checklist-items-list">
                          {maintenanceChecklists.map((item, index) => (
                            <li key={index} className="checklist-item">
                              <input
                                type="checkbox"
                                checked={item.completed || false}
                                onChange={() => handleToggleMaintenanceChecklistItem(index)}
                              />
                              <span className="checklist-item-text">
                                {item.text}
                              </span>
                              <button
                                onClick={() => handleRemoveMaintenanceChecklistItem(index)}
                                className="checklist-item-delete"
                                title="Delete item"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Add New Item Form */}
                    <div className="checklist-add-form">
                      <input
                        type="text"
                        value={newMaintenanceItem}
                        onChange={(e) => setNewMaintenanceItem(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddMaintenanceChecklistItem();
                          }
                        }}
                        placeholder="Add new maintenance item..."
                      />
                      <button
                        onClick={handleAddMaintenanceChecklistItem}
                        className="checklist-add-button"
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                )}

                {!isEditing && maintenanceChecklists.length === 0 && (
                  <div className="checklist-empty-state">
                    <p>📌 No maintenance checklist items created yet</p>
                  </div>
                )}
              </div>
            </div>

          {/* Compliance Checklist */}
          <div className="modal-section">
              <h4 className="modal-section-title">✓ Compliance Checklist</h4>
              <div className="checklist-container">
                {/* View Mode - Read Existing Checklists */}
                {!isEditing && complianceChecklists.length > 0 && (
                  <div>
                    <div className="checklist-existing-header">Existing Items:</div>
                    <ul className="checklist-items-list">
                      {complianceChecklists.map((item, index) => (
                        <li key={index} className="checklist-item read-only">
                          <input
                            type="checkbox"
                            checked={item.completed || false}
                            readOnly
                          />
                          <span className="checklist-item-text">
                            {item.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Edit Mode - Full CRUD */}
                {isEditing && (
                  <div>
                    {complianceChecklists.length > 0 && (
                      <div>
                        <div className="checklist-existing-header">Existing Items:</div>
                        <ul className="checklist-items-list">
                          {complianceChecklists.map((item, index) => (
                            <li key={index} className="checklist-item">
                              <input
                                type="checkbox"
                                checked={item.completed || false}
                                onChange={() => handleToggleComplianceChecklistItem(index)}
                              />
                              <span className="checklist-item-text">
                                {item.text}
                              </span>
                              <button
                                onClick={() => handleRemoveComplianceChecklistItem(index)}
                                className="checklist-item-delete"
                                title="Delete item"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Add New Item Form */}
                    <div className="checklist-add-form">
                      <input
                        type="text"
                        value={newComplianceItem}
                        onChange={(e) => setNewComplianceItem(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddComplianceChecklistItem();
                          }
                        }}
                        placeholder="Add new compliance item..."
                      />
                      <button
                        onClick={handleAddComplianceChecklistItem}
                        className="checklist-add-button"
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                )}

                {!isEditing && complianceChecklists.length === 0 && (
                  <div className="checklist-empty-state">
                    <p>✅ No compliance checklist items created yet</p>
                  </div>
                )}
              </div>
            </div>

          {/* Maintenance History */}
          <div className="modal-section">
            <h4 className="modal-section-title">Maintenance History</h4>
            <MaintenanceHistory assetId={asset._id} />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="asset-modal-footer">
          {!isEditing ? (
            <>
              <button onClick={() => setIsEditing(true)} className="btn btn-primary">
                Edit Asset
              </button>
              {asset.status !== 'disposed' && (
                <button
                  onClick={() => {
                    onClose();
                    navigate('/issues', {
                      state: {
                        prefillAsset: {
                          assetId: asset._id,
                          assetName: asset.assetName,
                          serialNumber: asset.serialNumber,
                          issueType: 'preventive'
                        }
                      }
                    });
                  }}
                  className="btn btn-warning"
                >
                  Report Issue / Maintenance
                </button>
              )}
              <button onClick={handleDelete} className="btn btn-danger">
                Delete
              </button>
              <button onClick={onClose} className="btn btn-secondary">
                Close
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSave} className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={handleCancelEdit} className="btn btn-secondary">
                Cancel
              </button>
            </>
          )}
        </div>

        {/* Edit Unit Modal */}
        {showUnitEditModal && selectedUnit && (
          <EditUnitModal
            asset={currentAsset}
            unit={selectedUnit}
            unitIndex={selectedUnitIndex}
            isOpen={showUnitEditModal}
            onClose={handleUnitEditClose}
            onUnitUpdated={handleUnitUpdated}
          />
        )}
      </div>
      </div>
   
  );
};

/* ======================== ALLOCATION DETAILS MODAL ======================== */
const AllocationDetailsModal = ({ asset, allocations, loading, onClose }) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    // Handle already formatted strings
    if (typeof duration === 'string' && !duration.includes('_')) {
      return duration;
    }
    
    // Format duration with underscores to readable format
    return duration
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const calculateRemainingDays = (assignedDate, returnDate, requestedDuration) => {
    // If already returned/released, show 0
    if (returnDate) {
      return '0';
    }

    // If no assigned date, return N/A
    if (!assignedDate) {
      return 'N/A';
    }

    // Parse duration string
    const durationMap = {
      '1_day': 1, '1_week': 7, '2_weeks': 14, '3_weeks': 21,
      '1_month': 30, '2_months': 60, '3_months': 90, '6_months': 180,
      'one_day': 1, 'one_week': 7, 'two_weeks': 14, 'three_weeks': 21,
      'one_month': 30, 'two_months': 60, 'three_months': 90, 'six_months': 180
    };

    // If no duration specified, return N/A
    if (!requestedDuration) {
      return 'N/A';
    }

    const durationDays = durationMap[requestedDuration];
    if (!durationDays || durationDays === 0) {
      return 'N/A';
    }

    // Calculate expected release date
    const expectedReleaseDate = new Date(assignedDate);
    expectedReleaseDate.setDate(expectedReleaseDate.getDate() + durationDays);

    // Calculate days remaining from today to expected release date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expectedReleaseDate.setHours(0, 0, 0, 0);
    
    const remainingMs = expectedReleaseDate - today;
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    if (remainingDays < 0) {
      return 'Overdue';
    }
    
    return remainingDays === 0 ? 'Today' : `${remainingDays} days`;
  };

  const getExpectedReleaseDate = (assignedDate, requestedDuration) => {
    if (!assignedDate || !requestedDuration) return null;

    const durationMap = {
      '1_day': 1, '1_week': 7, '2_weeks': 14, '3_weeks': 21,
      '1_month': 30, '2_months': 60, '3_months': 90, '6_months': 180,
      'one_day': 1, 'one_week': 7, 'two_weeks': 14, 'three_weeks': 21,
      'one_month': 30, 'two_months': 60, 'three_months': 90, 'six_months': 180
    };

    const durationDays = durationMap[requestedDuration];
    if (!durationDays || durationDays === 0) return null;

    const expectedDate = new Date(assignedDate);
    expectedDate.setDate(expectedDate.getDate() + durationDays);
    return expectedDate;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content allocation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{asset.assetName}</h2>
            <p className="allocation-subtitle">Total Quantity: {asset.quantity}</p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-text">Loading allocation details...</div>
          ) : (
            <>
              {/* Available Nos Section */}
              <div className="allocation-section">
                <h3 className="section-title">Available Nos ({allocations.available.length})</h3>
                <div className="allocation-table-wrapper">
                  {allocations.available.length === 0 ? (
                    <p className="no-data">No available units</p>
                  ) : (
                    <table className="allocation-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Unit ID</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocations.available.map((unit, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td><strong>{unit.unitId}</strong></td>
                            <td>
                              <span className={`status-badge unit-status-${unit.status}`}>
                                {unit.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Allocated Nos Section */}
              <div className="allocation-section">
                <h3 className="section-title">Allocated Nos ({allocations.allocated.length})</h3>
                <div className="allocation-table-wrapper">
                  {allocations.allocated.length === 0 ? (
                    <p className="no-data">No allocated units</p>
                  ) : (
                    <table className="allocation-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Unit ID</th>
                          <th>User Name</th>
                          <th>Department</th>
                          <th>Desk No</th>
                          <th>Allocated On</th>
                          <th>Duration</th>
                          <th>Allocated Till</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocations.allocated.map((alloc, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td><strong>{alloc.unitId}</strong></td>
                            <td>{alloc.userName}</td>
                            <td>{alloc.userDepartment}</td>
                            <td>{alloc.deskLocation}</td>
                            <td>{formatDate(alloc.allocatedOn)}</td>
                            <td>{formatDuration(alloc.duration)}</td>
                            <td>
                              <div className="allocation-till-info">
                                <span className="till-date">
                                  {getExpectedReleaseDate(alloc.allocatedOn, alloc.duration)
                                    ? formatDate(getExpectedReleaseDate(alloc.allocatedOn, alloc.duration))
                                    : 'N/A'}
                                </span>
                                <span className="remaining-days">
                                  {calculateRemainingDays(alloc.allocatedOn, alloc.allocatedTill, alloc.duration)}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="status-badge-active">Active</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewAssets;
